import { AdSlotType } from '../../../advertising/AdTypes';
import { moduleSides, ROADSIDE_TAG } from '../segmentBuilders';
import type { AdvertisingSlotDefinition, DecorationSocket, SegmentDefinition, TrackArea } from '../SegmentDefinition';
import { SEG_STRAIGHT_40M } from './SEG_STRAIGHT_40M';

/**
 * SEG_AD_GANTRY_40M – "Werbebrücke": the long straight SEG_STRAIGHT_40M (same obstacle rows and
 * bottle lines) with a steel lattice gantry spanning the road at its middle. The banner on the
 * gantry is a bookable ad slot (AD_GANTRY_BANNER, always filled) – far visible, exchanged by texture.
 *
 *   Gantry (ENV_AD_GANTRY_A, tools/blender/assets/ENV_AdGantry_A.py), local frame: origin = track
 *   centre on the ground at z 20, x across (−x = runner's left), y up, z along the travel direction,
 *   symmetric about its z = 0. Towers at x ±5.2 (lattice 0.5 × 0.5 m, 8.9 m high) on concrete
 *   ballast 0.9 × 0.9 × 0.6 m → footprint x 4.75…5.65, z ±0.45, outside the fence (±4.2).
 *   Truss beams y 4.9–5.4 and 8.1–8.6 – nothing hangs below the double-jump head height (4.8 m);
 *   banner 9.9 × 2.5 m between the towers, bottom edge at y 5.5, facing −z. No collision – purely decorative.
 *
 *   Roadside: the four bays touching a tower shrink to ground props (they stay clear of the towers);
 *   the other bays, backdrop, cranes, fence ads and event sockets are those of every straight.
 *   Placement (LevelGenerator, landmark rules): behind a quiet module (no junction exit, roof, overhead
 *   passage or scripted event), at least 120 m apart, in place of plain straights – see AD_GANTRY.placement.
 */
export const AD_GANTRY = {
  assetId: 'ENV_AD_GANTRY_A',
  /** Gantry axis: middle of the module (path z). */
  z: 20,
  tower: {
    /** Tower centre lines (±x). */
    x: 5.2,
    /** Square lattice section. */
    section: 0.5,
    /** Overall height incl. the top truss (raised by 0.5 m on 2026-10-02: double-jump clearance). */
    height: 8.9,
  },
  /** Concrete ballast block under each tower (x × y × z, meters). */
  ballast: { width: 0.9, height: 0.6, depth: 0.9 },
  /** Decorations keep this much air around a tower footprint (ballast edge). */
  towerClearance: 0.5,
  /**
   * Truss beams between the towers (bottom/top heights, depth along z). The lower beam's underside
   * stays above the runner's highest reach (jump + double jump + body = 4.8 m, overheadReach).
   */
  beams: { lower: { from: 4.9, to: 5.4 }, top: { from: 8.1, to: 8.6 }, depth: 0.5 },
  banner: {
    /**
     * Printed area between the inner tower faces (|x| 4.95): 9.9 m wide – the 4:1 creative
     * (AD_FORMATS) is squeezed by 1 %, invisible; 10 m would reach into the tower chords.
     */
    width: 9.9,
    height: 2.5,
    /** Banner bottom edge ("Banner-Unterkante 5,5 m"; the lower truss beam below it starts at 4.9 m). */
    bottom: 5.5,
    /** Front face of the dark banner backing in the GLB (gantry-local z). */
    backingZ: -0.1,
    /** The game's ad surface hangs just in front of the backing (gantry-local z), facing −z. */
    // 6 cm in front (was 2 cm: flickered against the backing in the distance)
  surfaceZ: -0.16,
  },
  /**
   * Placement (user 2026-10-02: "wesentlich öfter", and keep the variety), LevelGenerator landmark
   * rules: the gantry replaces plain straights only – junctions, curves, tunnels and hazards keep
   * their frequency. ≈ 3.9 per km, median gap 220 m (tests/unit/junctionRate.test.ts).
   */
  placement: {
    /** First gantry right after the safe start (2 × 20 m) – placed there directly. */
    minDistance: 100,
    /** Minimum track between two gantries (meters, module start to module start). */
    minSpacing: 120,
    /**
     * Open straight required before the module: none – the gantry stands mid-module, so its own 20 m
     * approach is straight; it may follow curves (the banner shows across the bend, ≥ 80 m ahead), and
     * on longer straights it is visible from the moment its module spawns.
     */
    sightLine: 0,
    /**
     * Calm track behind the module. 0: anything may follow – the runner has passed the banner before
     * the decision window of a junction right behind it opens; set pieces keep their frequency.
     */
    calmAfter: 0,
  },
} as const;

/** Banner faces the approaching runner (−z). */
const FACE_APPROACH = Math.PI;

/** Contract footprint of one tower incl. its ballast block (path coordinates). */
export function gantryTowerFootprint(side: -1 | 1): TrackArea {
  const { tower, ballast, z } = AD_GANTRY;
  return {
    xFrom: side * tower.x - ballast.width / 2,
    xTo: side * tower.x + ballast.width / 2,
    zFrom: z - ballast.depth / 2,
    zTo: z + ballast.depth / 2,
  };
}

/** Floor-plan rectangle a decoration socket may fill (width along the socket's local x). */
export function socketArea(socket: DecorationSocket): TrackArea {
  const cos = Math.abs(Math.cos(socket.yaw));
  const sin = Math.abs(Math.sin(socket.yaw));
  const { width, depth } = socket.maxFootprint;
  const halfX = (cos * width + sin * depth) / 2;
  const halfZ = (sin * width + cos * depth) / 2;
  return { xFrom: socket.position.x - halfX, xTo: socket.position.x + halfX, zFrom: socket.position.z - halfZ, zTo: socket.position.z + halfZ };
}

/** A socket's area reaches into a tower footprint or its clearance. */
export function touchesGantryTower(socket: DecorationSocket): boolean {
  const area = socketArea(socket);
  const air = AD_GANTRY.towerClearance;
  return ([-1, 1] as const).some((side) => {
    const tower = gantryTowerFootprint(side);
    return area.xFrom < tower.xTo + air && tower.xFrom - air < area.xTo && area.zFrom < tower.zTo + air && tower.zFrom - air < area.zTo;
  });
}

const LENGTH = SEG_STRAIGHT_40M.length;
/** Roadside bays that would stand on a tower – they become single ground props. */
const TOWER_BAYS = moduleSides(LENGTH)
  .decorationSockets.filter((socket) => socket.tags.includes(ROADSIDE_TAG) && touchesGantryTower(socket))
  .map((socket) => socket.id);
const SIDES = moduleSides(LENGTH, { groundProps: TOWER_BAYS });

const GANTRY_BANNER: AdvertisingSlotDefinition = {
  id: 'AD_GANTRY_BANNER',
  slotType: AdSlotType.GantryBanner,
  position: { x: 0, y: AD_GANTRY.banner.bottom + AD_GANTRY.banner.height / 2, z: AD_GANTRY.z + AD_GANTRY.banner.surfaceZ },
  yaw: FACE_APPROACH,
  width: AD_GANTRY.banner.width,
  height: AD_GANTRY.banner.height,
  // the gantry always carries a banner (fence slots: slotFillRate)
  fillRate: 1,
};

export const SEG_AD_GANTRY_40M: SegmentDefinition = {
  ...SEG_STRAIGHT_40M,
  id: 'SEG_AD_GANTRY_40M',
  displayName: 'Gerade 40 m mit Werbebrücke',
  extraVisuals: [...(SEG_STRAIGHT_40M.extraVisuals ?? []), { assetId: AD_GANTRY.assetId, position: { x: 0, y: 0, z: AD_GANTRY.z } }],

  // safety net: nothing that still reaches a tower stays (ground props stand clear today)
  decorationSockets: SIDES.decorationSockets.filter((socket) => !touchesGantryTower(socket)),
  dynamicEventSockets: SIDES.dynamicEventSockets,
  advertisingSlots: [...SIDES.advertisingSlots, GANTRY_BANNER],

  generation: {
    // unused: a landmark takes the pick share of the plain straights, overdue it is placed directly (LevelGenerator)
    weight: 1,
    minDistance: AD_GANTRY.placement.minDistance,
    cooldownDistance: AD_GANTRY.placement.minSpacing,
    // at the latest every 240 m (2026-10-05): the harder mix (more hazard and special modules, fewer plain straights)
    // must not thin out the gantries – their banners are sold
    landmark: { sightLine: AD_GANTRY.placement.sightLine, calmAfter: AD_GANTRY.placement.calmAfter, targetSpacing: 240 },
    tags: ['straight', 'ground', 'long', 'landmark', 'advertising', 'bus-road'],
  },
};
